import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CalendarDaysIcon, 
  MapPinIcon, 
  ClockIcon, 
  UserGroupIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

interface EventDetails {
  id: string;
  title: string;
  description: string;
  longDescription: string;
  date: string;
  endDate?: string;
  startTime: string;
  endTime?: string;
  location: string;
  venue: string;
  address: string;
  price: number;
  category: string;
  image: string;
  availableSpots: number;
  totalSpots: number;
  instructor?: string;
  prerequisites: string[];
  inclusions: string[];
  agenda: Array<{
    time: string;
    title: string;
    description: string;
  }>;
}

const EventDetailsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Mock event details - Replace with API call
  const mockEvents: Record<string, EventDetails> = useMemo(() => ({
    '1': {
      id: '1',
      title: 'Advanced Collision Repair Workshop',
      description: 'Comprehensive training on the latest collision repair techniques and technologies.',
      longDescription: 'This intensive workshop covers advanced collision repair techniques including frame straightening, advanced welding methods, and the latest in repair technology. Participants will gain hands-on experience with industry-standard equipment and learn best practices for complex repairs. The course is designed for experienced technicians looking to advance their skills and stay current with industry developments.',
      date: '2026-05-15',
      startTime: '09:00',
      endTime: '17:00',
      location: 'Chicago, IL',
      venue: 'Crash Champions Training Center',
      address: '1234 Industrial Blvd, Chicago, IL 60601',
      price: 299,
      category: 'Workshop',
      image: 'event-1.jpg',
      availableSpots: 15,
      totalSpots: 30,
      instructor: 'Master Technician John Smith',
      prerequisites: [
        'Minimum 2 years collision repair experience',
        'Basic welding knowledge',
        'Safety certification required'
      ],
      inclusions: [
        'All materials and tools provided',
        'Lunch and refreshments',
        'Certificate of completion',
        'Take-home reference materials',
        '8 hours of continuing education credit'
      ],
      agenda: [
        {
          time: '09:00 AM',
          title: 'Registration & Welcome',
          description: 'Check-in and course overview'
        },
        {
          time: '09:30 AM',
          title: 'Advanced Welding Techniques',
          description: 'Hands-on training with latest welding equipment'
        },
        {
          time: '11:00 AM',
          title: 'Frame Straightening Methods',
          description: 'Modern approach to structural repairs'
        },
        {
          time: '12:30 PM',
          title: 'Lunch Break',
          description: 'Networking opportunity with peers'
        },
        {
          time: '01:30 PM',
          title: 'Technology Integration',
          description: 'Digital tools and estimation software'
        },
        {
          time: '03:00 PM',
          title: 'Quality Control Process',
          description: 'Ensuring repair standards and safety'
        },
        {
          time: '04:30 PM',
          title: 'Q&A and Certification',
          description: 'Final questions and certificate presentation'
        }
      ]
    },
    '2': {
      id: '2',
      title: 'Paint Technology Seminar',
      description: 'Discover cutting-edge paint technologies and application techniques.',
      longDescription: 'Stay ahead of the curve with the latest paint technology innovations. This seminar covers waterborne paints, advanced color matching techniques, and environmental considerations in modern paint systems. Perfect for paint technicians and shop managers.',
      date: '2026-05-22',
      startTime: '10:00',
      endTime: '16:00',
      location: 'Dallas, TX',
      venue: 'Dallas Convention Center',
      address: '650 S Griffin St, Dallas, TX 75202',
      price: 199,
      category: 'Seminar',
      image: '1.jpg',
      availableSpots: 8,
      totalSpots: 25,
      instructor: 'Paint Specialist Maria Rodriguez',
      prerequisites: [
        'Basic paint knowledge',
        'Shop experience preferred'
      ],
      inclusions: [
        'Paint samples and testing kit',
        'Color matching guide',
        'Certificate of attendance',
        'Networking lunch'
      ],
      agenda: [
        {
          time: '10:00 AM',
          title: 'Registration',
          description: 'Welcome and materials distribution'
        },
        {
          time: '10:30 AM',
          title: 'Modern Paint Systems',
          description: 'Overview of current paint technologies'
        },
        {
          time: '12:00 PM',
          title: 'Lunch & Networking',
          description: 'Connect with industry professionals'
        },
        {
          time: '01:00 PM',
          title: 'Color Matching Workshop',
          description: 'Hands-on color matching techniques'
        },
        {
          time: '03:00 PM',
          title: 'Environmental Considerations',
          description: 'Compliance and best practices'
        }
      ]
    }
    // Add more events as needed
  }), []);

  useEffect(() => {
    if (eventId) {
      // Simulate API call
      setTimeout(() => {
        const eventData = mockEvents[eventId];
        if (eventData) {
          setEvent(eventData);
        }
        setLoading(false);
      }, 800);
    }
  }, [eventId, mockEvents]);

  const formatDate = (dateString: string, endDateString?: string) => {
    const date = new Date(dateString);
    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    if (endDateString) {
      const endDate = new Date(endDateString);
      const endFormatted = endDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      return `${formatted} - ${endFormatted}`;
    }

    return formatted;
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours);
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-8">The event you're looking for doesn't exist.</p>
          <Link
            to="/events"
            className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Browse All Events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <div className="relative">
        <div className="aspect-w-16 aspect-h-6 lg:aspect-h-4">
          <img
            src={`/${event.image}`}
            alt={event.title}
            className={`w-full h-64 lg:h-96 object-cover transition-opacity duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <div className="mb-4">
                <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {event.category}
                </span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                {event.title}
              </h1>
              <p className="text-xl lg:text-2xl text-gray-200 max-w-3xl">
                {event.description}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Event Details */}
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="w-5 h-5 text-primary-600 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Date</div>
                      <div className="font-medium">{formatDate(event.date, event.endDate)}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <ClockIcon className="w-5 h-5 text-primary-600 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Time</div>
                      <div className="font-medium">
                        {formatTime(event.startTime)}
                        {event.endTime && ` - ${formatTime(event.endTime)}`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <MapPinIcon className="w-5 h-5 text-primary-600 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Location</div>
                      <div className="font-medium">{event.location}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <UserGroupIcon className="w-5 h-5 text-primary-600 mr-3" />
                    <div>
                      <div className="text-sm text-gray-500">Available</div>
                      <div className={`font-medium ${
                        event.availableSpots < 10 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {event.availableSpots}/{event.totalSpots}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Event</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {event.longDescription}
                </p>
              </div>

              {/* Venue Information */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Venue Information</h2>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.venue}</h3>
                  <p className="text-gray-600">{event.address}</p>
                  <div className="mt-4">
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(event.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View on Google Maps →
                    </a>
                  </div>
                </div>
              </div>

              {/* Agenda */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Event Agenda</h2>
                <div className="space-y-4">
                  {event.agenda.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-20 text-primary-600 font-medium">
                          {item.time}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="sticky top-8"
            >
              {/* Booking Card */}
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    ${event.price}
                  </div>
                  <p className="text-gray-600">per person</p>
                </div>

                {event.availableSpots > 0 ? (
                  <>
                    <Link
                      to={`/checkout/${event.id}`}
                      className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors mb-3 block text-center"
                    >
                      Book Now
                    </Link>
                    {event.availableSpots < 10 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center">
                          <InformationCircleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                          <span className="text-sm text-yellow-800 font-medium">
                            Only {event.availableSpots} spots remaining!
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <button
                    disabled
                    className="w-full bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed mb-3"
                  >
                    Fully Booked
                  </button>
                )}

                <Link
                  to="/events"
                  className="w-full border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors block text-center"
                >
                  Browse Other Events
                </Link>
              </div>

              {/* Instructor */}
              {event.instructor && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Instructor</h3>
                  <p className="text-gray-700">{event.instructor}</p>
                </div>
              )}

              {/* Prerequisites */}
              {event.prerequisites.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Prerequisites</h3>
                  <ul className="space-y-2">
                    {event.prerequisites.map((prereq, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{prereq}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Inclusions */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">What's Included</h3>
                <ul className="space-y-2">
                  {event.inclusions.map((inclusion, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{inclusion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventDetailsPage;